import React, { useState, useEffect, useRef } from 'react';
import { RUN_TYPES, RUN_EFFORTS, describeRun, runTargetText, emptyWorkoutForm } from '../utils/runWorkout';

const SimpleProgramBuilder = ({ 
  onClose, 
  onSave, 
  editingProgram = null,
  exerciseLibrary = []
}) => {
  const [step, setStep] = useState(1);
  const [previewMode, setPreviewMode] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [draggedExerciseIndex, setDraggedExerciseIndex] = useState(null);
  const autoSaveTimerRef = useRef(null);
  
  // Initialize program state with proper structure
  const initializeProgram = () => {
    if (editingProgram) {
      // Editing existing program - preserve all fields exactly as saved
      console.log('Loading program for editing:', editingProgram);
      return {
        title: editingProgram.title || '',
        description: editingProgram.description || '',
        duration: editingProgram.duration || '4 Weeks',
        frequency: editingProgram.frequency || '4x/week',
        level: editingProgram.level !== undefined ? editingProgram.level : 'Intermediate',
        equipment: editingProgram.equipment || 'Full gym access',
        goals: editingProgram.goals || '',
        icon: editingProgram.icon || '🏋️',
        icon_bg: editingProgram.icon_bg || 'bg-orange-100',
        color: editingProgram.color || 'orange',
        weeks: editingProgram.weeks || [
          { week: 1, focus: '', workouts: [] },
          { week: 2, focus: '', workouts: [] },
          { week: 3, focus: '', workouts: [] },
          { week: 4, focus: '', workouts: [] }
        ]
      };
    }
    // New program
    return {
      title: '',
      description: '',
      duration: '4 Weeks',
      frequency: '4x/week',
      level: 'Intermediate',
      equipment: 'Full gym access',
      goals: '',
      weeks: [
        { week: 1, focus: '', workouts: [] },
        { week: 2, focus: '', workouts: [] },
        { week: 3, focus: '', workouts: [] },
        { week: 4, focus: '', workouts: [] }
      ]
    };
  };

  const [program, setProgram] = useState(initializeProgram());

  const [currentWeek, setCurrentWeek] = useState(0);
  const [currentWorkout, setCurrentWorkout] = useState(null);
  const [editingWorkoutIndex, setEditingWorkoutIndex] = useState(null);
  const [workoutForm, setWorkoutForm] = useState(emptyWorkoutForm());

  const [exerciseForm, setExerciseForm] = useState({
    name: '',
    sets: [{ setNumber: 1, reps: '12', type: 'normal', isTimed: false }],
    restTime: '60s',
    supersetGroup: '',
    tempo: '',
    videoUrl: '',
    instructions: '',
    muscleGroups: ''
  });

  const [editingExerciseIndex, setEditingExerciseIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showCreateExerciseModal, setShowCreateExerciseModal] = useState(false);
  const [localExerciseLibrary, setLocalExerciseLibrary] = useState(exerciseLibrary);

  // Load exercise library from localStorage on mount
  useEffect(() => {
    try {
      const savedExercises = localStorage.getItem('exerciseLibrary');
      if (savedExercises) {
        const parsed = JSON.parse(savedExercises);
        // Validate and fix each exercise entry to ensure muscleGroups is always an array
        const validated = parsed.map(ex => ({
          ...ex,
          muscleGroups: Array.isArray(ex.muscleGroups) 
            ? ex.muscleGroups 
            : (typeof ex.muscleGroups === 'string' && ex.muscleGroups 
              ? ex.muscleGroups.split(',').map(m => m.trim()).filter(m => m) 
              : [])
        }));
        setLocalExerciseLibrary(validated);
        // Write back validated data
        localStorage.setItem('exerciseLibrary', JSON.stringify(validated));
      }
    } catch (e) {
      console.warn('Failed to load exercise library:', e);
    }
  }, []);

  // Helper: count all exercises across all weeks
  const getProgramCounts = (prog) => {
    const totalWorkouts = (prog.weeks || []).reduce((sum, w) => sum + (w.workouts?.length || 0), 0);
    const totalExercises = (prog.weeks || []).reduce((sum, w) => 
      sum + (w.workouts || []).reduce((s, wo) => s + (wo.exercises?.length || 0), 0), 0);
    const weeksWithWorkouts = (prog.weeks || []).filter(w => w.workouts?.length > 0).length;
    return { totalWorkouts, totalExercises, weeksWithWorkouts, totalWeeks: (prog.weeks || []).length };
  };

  // Instant auto-save to localStorage on every change
  useEffect(() => {
    if (!program.title) return;
    try {
      localStorage.setItem('programDraft', JSON.stringify({
        program,
        timestamp: new Date().toISOString()
      }));
      setLastSaved(new Date());
    } catch (e) {
      console.error('Auto-save failed:', e);
    }
  }, [program]);

  // Load draft on mount
  useEffect(() => {
    if (!editingProgram) {
      try {
        const draft = localStorage.getItem('programDraft');
        if (draft) {
          const { program: draftProgram, timestamp } = JSON.parse(draft);
          const draftAge = Date.now() - new Date(timestamp).getTime();
          // Only load if draft is less than 24 hours old
          if (draftAge < 24 * 60 * 60 * 1000) {
            const loadDraft = window.confirm('Found an unsaved draft. Would you like to continue where you left off?');
            if (loadDraft) {
              setProgram(draftProgram);
              setLastSaved(new Date(timestamp));
            }
          }
        }
      } catch (e) {
        console.error('Failed to load draft:', e);
      }
    }
  }, []);

  // Add exercise to current workout (or update in-place if editing)
  const addExercise = () => {
    if (!exerciseForm.name) return;

    if (editingExerciseIndex !== null) {
      // UPDATE IN-PLACE: replace the exercise at its original position
      const updatedExercises = [...workoutForm.exercises];
      updatedExercises[editingExerciseIndex] = {
        ...exerciseForm,
        id: updatedExercises[editingExerciseIndex].id // preserve original ID
      };
      setWorkoutForm({
        ...workoutForm,
        exercises: updatedExercises
      });
      setEditingExerciseIndex(null);
    } else {
      // ADD NEW: append to end
      const newExercise = {
        ...exerciseForm,
        id: `ex-${Date.now()}`
      };
      setWorkoutForm({
        ...workoutForm,
        exercises: [...workoutForm.exercises, newExercise]
      });
    }

    // Auto-save to library if not already there (by name match)
    const alreadyInLibrary = localExerciseLibrary.some(
      ex => ex.name.toLowerCase() === exerciseForm.name.toLowerCase()
    );
    if (!alreadyInLibrary) {
      const libraryExercise = {
        id: `lib-${Date.now()}`,
        name: exerciseForm.name,
        muscleGroups: exerciseForm.muscleGroups 
          ? exerciseForm.muscleGroups.split(',').map(m => m.trim()).filter(m => m) 
          : [],
        videoUrl: exerciseForm.videoUrl || '',
        instructions: exerciseForm.instructions || '',
        defaultRestTime: exerciseForm.restTime || '60s',
        defaultTempo: exerciseForm.tempo || ''
      };
      const updatedLibrary = [...localExerciseLibrary, libraryExercise];
      setLocalExerciseLibrary(updatedLibrary);
      try {
        localStorage.setItem('exerciseLibrary', JSON.stringify(updatedLibrary));
      } catch (e) {
        console.warn('Failed to save exercise to library:', e);
      }
    }

    // Reset form
    setExerciseForm({
      name: '',
      sets: [{ setNumber: 1, reps: '12', type: 'normal', isTimed: false }],
      restTime: '60s',
      supersetGroup: '',
      tempo: '',
      videoUrl: '',
      instructions: '',
      muscleGroups: ''
    });
    setSearchQuery('');
  };

  // Save workout to week
  const saveWorkout = () => {
    const isRun = workoutForm.workoutType === 'run';
    if (!workoutForm.title || (!isRun && workoutForm.exercises.length === 0)) return;

    const updatedWeeks = [...program.weeks];
    const workout = {
      ...workoutForm,
      id: workoutForm.id || `workout-${Date.now()}`,
      exercises: isRun ? [] : workoutForm.exercises,
      equipment: Array.isArray(workoutForm.equipment) 
        ? workoutForm.equipment 
        : (workoutForm.equipment ? workoutForm.equipment.split(',').map(e => e.trim()).filter(e => e) : [])
    };

    // Ensure week has a title
    if (!updatedWeeks[currentWeek].title) {
      updatedWeeks[currentWeek].title = `Week ${updatedWeeks[currentWeek].week}`;
    }

    if (editingWorkoutIndex !== null) {
      // Update existing workout
      updatedWeeks[currentWeek].workouts[editingWorkoutIndex] = workout;
    } else {
      // Add new workout
      updatedWeeks[currentWeek].workouts.push(workout);
    }
    
    setProgram({ ...program, weeks: updatedWeeks });

    // Reset
    setWorkoutForm(emptyWorkoutForm());
    setCurrentWorkout(null);
    setEditingWorkoutIndex(null);
  };

  // Remove exercise
  const removeExercise = (exerciseId) => {
    setWorkoutForm({
      ...workoutForm,
      exercises: workoutForm.exercises.filter(ex => ex.id !== exerciseId)
    });
  };

  // Link two consecutive exercises as a superset
  const linkExercises = (idx) => {
    const exercises = [...workoutForm.exercises];
    const ex1 = exercises[idx];
    const ex2 = exercises[idx + 1];
    if (!ex1 || !ex2) return;

    // Find the next available superset letter
    const usedLetters = exercises
      .map(ex => ex.supersetGroup ? ex.supersetGroup.charAt(0) : null)
      .filter(Boolean);
    
    let letter;
    if (ex1.supersetGroup) {
      // First exercise already in a group — add second to same group
      letter = ex1.supersetGroup.charAt(0);
      const maxNum = exercises
        .filter(ex => ex.supersetGroup?.charAt(0) === letter)
        .map(ex => parseInt(ex.supersetGroup.charAt(1)) || 0)
        .reduce((max, n) => Math.max(max, n), 0);
      exercises[idx + 1] = { ...ex2, supersetGroup: `${letter}${maxNum + 1}` };
    } else if (ex2.supersetGroup) {
      // Second exercise already in a group — add first before it
      letter = ex2.supersetGroup.charAt(0);
      const nums = exercises
        .filter(ex => ex.supersetGroup?.charAt(0) === letter)
        .map(ex => parseInt(ex.supersetGroup.charAt(1)) || 0);
      const minNum = Math.min(...nums);
      exercises[idx] = { ...ex1, supersetGroup: `${letter}${Math.max(minNum - 1, 0) || 1}` };
      // Re-number the group sequentially
      let count = 1;
      exercises.forEach((ex, i) => {
        if (ex.supersetGroup?.charAt(0) === letter) {
          exercises[i] = { ...ex, supersetGroup: `${letter}${count}` };
          count++;
        }
      });
    } else {
      // Neither has a group — create new one with next available letter
      const nextLetter = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
        .find(l => !usedLetters.includes(l)) || 'A';
      exercises[idx] = { ...ex1, supersetGroup: `${nextLetter}1` };
      exercises[idx + 1] = { ...ex2, supersetGroup: `${nextLetter}2` };
    }

    setWorkoutForm({ ...workoutForm, exercises });
  };

  // Unlink two consecutive exercises from their superset
  const unlinkExercises = (idx) => {
    const exercises = [...workoutForm.exercises];
    const ex1 = exercises[idx];
    const ex2 = exercises[idx + 1];
    if (!ex1 || !ex2) return;

    const letter = ex1.supersetGroup?.charAt(0);
    
    // Count how many exercises share this superset letter
    const groupMembers = exercises.filter(ex => ex.supersetGroup?.charAt(0) === letter);
    
    if (groupMembers.length <= 2) {
      // Only 2 in the group — remove superset from both
      exercises.forEach((ex, i) => {
        if (ex.supersetGroup?.charAt(0) === letter) {
          exercises[i] = { ...ex, supersetGroup: '' };
        }
      });
    } else {
      // More than 2 — just remove the second exercise from the group
      exercises[idx + 1] = { ...ex2, supersetGroup: '' };
      // Re-number remaining
      let count = 1;
      exercises.forEach((ex, i) => {
        if (ex.supersetGroup?.charAt(0) === letter) {
          exercises[i] = { ...ex, supersetGroup: `${letter}${count}` };
          count++;
        }
      });
    }

    setWorkoutForm({ ...workoutForm, exercises });
  };

  // Move exercise up/down
  const moveExercise = (index, direction) => {
    const exercises = [...workoutForm.exercises];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= exercises.length) return;

    [exercises[index], exercises[newIndex]] = [exercises[newIndex], exercises[index]];
    setWorkoutForm({ ...workoutForm, exercises });
  };

  // Load exercise from library
  const selectFromLibrary = (libExercise) => {
    setExerciseForm({
      ...exerciseForm,
      name: libExercise.name,
      videoUrl: libExercise.videoUrl || '',
      instructions: libExercise.instructions || '',
      restTime: libExercise.defaultRestTime || '60s',
      tempo: libExercise.defaultTempo || '',
      muscleGroups: Array.isArray(libExercise.muscleGroups) ? libExercise.muscleGroups.join(', ') : ''
    });
    setSearchQuery(libExercise.name);
    setShowSuggestions(false);
  };

  // Save exercise to library
  const saveToLibrary = () => {
    if (!exerciseForm.name) {
      alert('⚠️ Please enter an exercise name first');
      return;
    }

    // Check if exercise already exists (update it if so)
    const existingIndex = localExerciseLibrary.findIndex(
      ex => ex.name.toLowerCase() === exerciseForm.name.toLowerCase()
    );

    const libraryExercise = {
      id: existingIndex >= 0 ? localExerciseLibrary[existingIndex].id : `lib-${Date.now()}`,
      name: exerciseForm.name,
      muscleGroups: exerciseForm.muscleGroups 
        ? (typeof exerciseForm.muscleGroups === 'string' 
          ? exerciseForm.muscleGroups.split(',').map(m => m.trim()).filter(m => m) 
          : exerciseForm.muscleGroups)
        : [],
      videoUrl: exerciseForm.videoUrl || '',
      instructions: exerciseForm.instructions || '',
      defaultRestTime: exerciseForm.restTime || '60s',
      defaultTempo: exerciseForm.tempo || ''
    };

    let updatedLibrary;
    if (existingIndex >= 0) {
      updatedLibrary = [...localExerciseLibrary];
      updatedLibrary[existingIndex] = libraryExercise;
    } else {
      updatedLibrary = [...localExerciseLibrary, libraryExercise];
    }

    setLocalExerciseLibrary(updatedLibrary);
    try {
      localStorage.setItem('exerciseLibrary', JSON.stringify(updatedLibrary));
      alert(`✅ "${exerciseForm.name}" ${existingIndex >= 0 ? 'updated in' : 'saved to'} Exercise Library!`);
    } catch (error) {
      console.error('Error saving to library:', error);
      alert('❌ Error saving to library. Please try again.');
    }
  };

  // Create new exercise for library
  const createNewExercise = (newExercise) => {
    const libraryExercise = {
      id: `lib-${Date.now()}`,
      ...newExercise,
      muscleGroups: newExercise.muscleGroups.split(',').map(m => m.trim()).filter(m => m)
    };

    const updatedLibrary = [...localExerciseLibrary, libraryExercise];
    setLocalExerciseLibrary(updatedLibrary);
    localStorage.setItem('exerciseLibrary', JSON.stringify(updatedLibrary));
    setShowCreateExerciseModal(false);
    alert(`✅ "${newExercise.name}" added to Exercise Library!`);
  };

  // Copy week
  const copyWeek = (fromWeek, toWeek) => {
    const updatedWeeks = [...program.weeks];
    updatedWeeks[toWeek].workouts = updatedWeeks[fromWeek].workouts.map(w => ({
      ...w,
      id: `workout-${Date.now()}-${Math.random()}`
    }));
    setProgram({ ...program, weeks: updatedWeeks });
  };

  const filteredExercises = localExerciseLibrary.filter(ex =>
    ex.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-lg w-full h-full max-w-[98vw] max-h-[98vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="p-6 border-b bg-gradient-to-r from-orange-500 to-red-500 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">
                {step === 1 ? '📋 Program Details' : '🏋️ Build Workouts'}
              </h2>
              <p className="text-sm opacity-90 mt-1">
                {step === 1 ? 'Set up your program basics' : `Building: ${program.title}`}
              </p>
            </div>
            <div className="flex items-center gap-4">
              {/* Live program counter */}
              {step === 2 && (
                <div className="bg-white/20 rounded-lg px-3 py-1.5 text-xs font-medium">
                  {getProgramCounts(program).totalWorkouts} workouts · {getProgramCounts(program).totalExercises} exercises
                </div>
              )}
              {/* Auto-save indicator */}
              {lastSaved && (
                <div className="text-xs opacity-75 flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-300 rounded-full inline-block"></span>
                  Draft saved
                </div>
              )}
              {/* Preview toggle */}
              {step === 2 && (
                <button
                  onClick={() => setPreviewMode(!previewMode)}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
                >
                  {previewMode ? '✏️ Edit Mode' : '👁️ Preview'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {step === 1 ? (
            /* STEP 1: Program Basics */
            <div className="p-8 max-w-2xl mx-auto">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Program Name *
                  </label>
                  <input
                    type="text"
                    value={program.title}
                    onChange={(e) => setProgram({ ...program, title: e.target.value })}
                    placeholder="e.g., January 2025 - Glute Builder"
                    className="w-full border border-slate-300 rounded-lg px-4 py-3 text-lg"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Duration
                    </label>
                    <select
                      value={program.duration}
                      onChange={(e) => setProgram({ ...program, duration: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-4 py-3"
                    >
                      <option>4 Weeks</option>
                      <option>6 Weeks</option>
                      <option>8 Weeks</option>
                      <option>12 Weeks</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Level
                    </label>
                    <select
                      value={program.level}
                      onChange={(e) => setProgram({ ...program, level: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-4 py-3"
                    >
                      <option>Beginner</option>
                      <option>Intermediate</option>
                      <option>Advanced</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    value={program.description}
                    onChange={(e) => setProgram({ ...program, description: e.target.value })}
                    placeholder="Describe what this program achieves..."
                    rows="4"
                    className="w-full border border-slate-300 rounded-lg px-4 py-3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Goals (comma separated)
                  </label>
                  <input
                    type="text"
                    value={program.goals}
                    onChange={(e) => setProgram({ ...program, goals: e.target.value })}
                    placeholder="e.g., Build Muscle, Lose Fat, Get Stronger"
                    className="w-full border border-slate-300 rounded-lg px-4 py-3"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Equipment Required *
                    </label>
                    <select
                      value={program.equipment || 'Full gym access'}
                      onChange={(e) => setProgram({ ...program, equipment: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-4 py-3"
                    >
                      <option>Full gym access</option>
                      <option>Dumbbells only</option>
                      <option>Minimal equipment</option>
                      <option>Bodyweight only</option>
                      <option>Resistance bands</option>
                      <option>Home gym setup</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Frequency *
                    </label>
                    <select
                      value={program.frequency || '4x/week'}
                      onChange={(e) => setProgram({ ...program, frequency: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-4 py-3"
                    >
                      <option>3x/week</option>
                      <option>4x/week</option>
                      <option>5x/week</option>
                      <option>6x/week</option>
                    </select>
                  </div>
                </div>

              </div>
            </div>
          ) : previewMode ? (
            /* PREVIEW MODE */
            <div className="p-8 max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-rose-50 via-white to-sage-50 rounded-2xl p-8">
                {/* Program Header */}
                <div className="mb-8">
                  <h1 className="text-4xl font-bold text-charcoal-800 mb-4">{program.title}</h1>
                  <p className="text-charcoal-600 mb-6">{program.description}</p>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Duration:</span>
                      <span>{program.duration}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Frequency:</span>
                      <span>{program.frequency}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Level:</span>
                      <span>{program.level}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">Equipment:</span>
                      <span>{program.equipment}</span>
                    </div>
                  </div>
                </div>

                {/* Weeks */}
                {program.weeks.map((week, weekIdx) => (
                  <div key={weekIdx} className="mb-8 bg-white rounded-xl p-6 shadow-md">
                    <h2 className="text-2xl font-bold text-charcoal-800 mb-2">Week {week.week}</h2>
                    {week.focus && <p className="text-charcoal-600 mb-4 italic">{week.focus}</p>}
                    
                    {/* Workouts */}
                    <div className="space-y-4">
                      {week.workouts.map((workout, workoutIdx) => (
                        <div key={workoutIdx} className="border-l-4 border-coral-500 pl-4">
                          <h3 className="text-lg font-semibold text-charcoal-800">{workout.title}</h3>
                          {workout.description && <p className="text-sm text-charcoal-600 mb-2">{workout.description}</p>}
                          <div className="text-xs text-charcoal-500 mb-3">
                            {workout.duration} • {workout.difficulty}
                          </div>
                          
                          {/* Exercises - with superset grouping / Run details */}
                          {workout.workoutType === 'run' ? (
                            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                              <div className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-1">
                                🏃 {workout.runType || 'Run'}
                              </div>
                              <div className="font-semibold text-charcoal-800">{runTargetText(workout)}</div>
                              {workout.effort && (
                                <div className="text-sm text-charcoal-600 mt-1">Effort: {workout.effort}</div>
                              )}
                              {workout.targetMode === 'intervals' && workout.recovery && (
                                <div className="text-sm text-charcoal-600 mt-1">Recovery: {workout.recovery}</div>
                              )}
                            </div>
                          ) : (
                          <div className="space-y-2">
                            {(() => {
                              const groups = [];
                              let ei = 0;
                              const exs = workout.exercises;
                              while (ei < exs.length) {
                                const ex = exs[ei];
                                const letter = ex.supersetGroup ? ex.supersetGroup.charAt(0) : null;
                                if (letter) {
                                  const group = [{ exercise: ex, idx: ei }];
                                  let ej = ei + 1;
                                  while (ej < exs.length && exs[ej].supersetGroup?.charAt(0) === letter) {
                                    group.push({ exercise: exs[ej], idx: ej });
                                    ej++;
                                  }
                                  groups.push(group.length > 1 ? { type: 'superset', letter, items: group } : { type: 'single', items: group });
                                  ei = ej;
                                } else {
                                  groups.push({ type: 'single', items: [{ exercise: ex, idx: ei }] });
                                  ei++;
                                }
                              }
                              return groups.map((group, gi) => {
                                if (group.type === 'superset') {
                                  return (
                                    <div key={`ss-${group.letter}-${gi}`} className="relative">
                                      <div className="flex items-center gap-1.5 mb-1">
                                        <span className="text-[10px] font-bold text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full flex items-center gap-1">
                                          🔗 Superset {group.letter}
                                        </span>
                                        <div className="flex-1 h-px bg-purple-200"></div>
                                      </div>
                                      <div className="border-l-[3px] border-purple-400 pl-3 space-y-0">
                                        {group.items.map((item, ii) => (
                                          <React.Fragment key={item.idx}>
                                            <div className="bg-purple-50 rounded-lg p-3">
                                              <div className="font-medium text-charcoal-800">
                                                <span className="text-purple-600 font-bold">{item.exercise.supersetGroup}</span> {item.exercise.name}
                                              </div>
                                              <div className="text-sm text-charcoal-600 mt-1">
                                                {item.exercise.sets.map((set, si) => (
                                                  <span key={si}>{set.reps}{si < item.exercise.sets.length - 1 ? ', ' : ''}</span>
                                                ))} • Rest: {item.exercise.restTime}
                                                {item.exercise.tempo && ` • Tempo: ${item.exercise.tempo}`}
                                              </div>
                                            </div>
                                            {ii < group.items.length - 1 && (
                                              <div className="flex items-center pl-2 py-0.5">
                                                <span className="text-[9px] text-purple-500 font-medium">↕ no rest</span>
                                              </div>
                                            )}
                                          </React.Fragment>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                } else {
                                  const item = group.items[0];
                                  return (
                                    <div key={item.idx} className="bg-gray-50 rounded-lg p-3">
                                      <div className="font-medium text-charcoal-800">
                                        {item.exercise.supersetGroup && <span className="text-orange-600 font-bold">[{item.exercise.supersetGroup}] </span>}
                                        {item.idx + 1}. {item.exercise.name}
                                      </div>
                                      <div className="text-sm text-charcoal-600 mt-1">
                                        {item.exercise.sets.map((set, si) => (
                                          <span key={si}>{set.reps}{si < item.exercise.sets.length - 1 ? ', ' : ''}</span>
                                        ))} • Rest: {item.exercise.restTime}
                                        {item.exercise.tempo && ` • Tempo: ${item.exercise.tempo}`}
                                      </div>
                                    </div>
                                  );
                                }
                              });
                            })()}
                          </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* STEP 2: Build Workouts */
            <div className="flex h-full">
              {/* Left: Week List */}
              <div className="w-64 border-r bg-slate-50 p-4 overflow-y-auto">
                <div className="mb-4">
                  <h3 className="font-bold text-slate-800 mb-2">📚 Exercise Library</h3>
                  <p className="text-xs text-slate-600 mb-2">{localExerciseLibrary.length} exercises saved</p>
                  <button
                    onClick={() => setShowCreateExerciseModal(true)}
                    className="w-full bg-purple-600 text-white px-3 py-2 rounded text-sm hover:bg-purple-700"
                  >
                    + Create New Exercise
                  </button>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-bold text-slate-800 mb-3">Weeks</h3>
                  {program.weeks.map((week, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentWeek(idx)}
                    className={`w-full text-left p-3 rounded-lg mb-2 ${
                      currentWeek === idx
                        ? 'bg-orange-500 text-white'
                        : 'bg-white hover:bg-slate-100'
                    }`}
                  >
                    <div className="font-semibold">Week {week.week}</div>
                    <div className="text-xs opacity-75">
                      {week.workouts.length} workout{week.workouts.length !== 1 ? 's' : ''}
                    </div>
                  </button>
                ))}
                </div>
              </div>

              {/* Middle: Workouts */}
              <div className="flex-1 p-6 overflow-y-auto">
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xl font-bold text-slate-800">
                      Week {program.weeks[currentWeek].week} Workouts
                    </h3>
                    <div className="flex gap-2">
                      {currentWeek > 0 && (
                        <button
                          onClick={() => copyWeek(currentWeek - 1, currentWeek)}
                          className="text-sm bg-blue-100 text-blue-700 px-3 py-1 rounded hover:bg-blue-200"
                        >
                          📋 Copy from Week {currentWeek}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Week Focus */}
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Week Focus (what members will see)
                    </label>
                    <input
                      type="text"
                      value={program.weeks[currentWeek].focus || ''}
                      onChange={(e) => {
                        const updatedWeeks = [...program.weeks];
                        updatedWeeks[currentWeek].focus = e.target.value;
                        setProgram({ ...program, weeks: updatedWeeks });
                      }}
                      placeholder="e.g., Foundation & Form - Building the basics"
                      className="w-full border border-slate-300 rounded-lg px-4 py-2 text-sm"
                    />
                  </div>
                </div>

                {/* Existing Workouts */}
                <div className="space-y-3 mb-4">
                  {program.weeks[currentWeek].workouts.map((workout, idx) => (
                    <div key={idx} className="bg-white border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-slate-800">{workout.title}</h4>
                          <p className="text-xs text-slate-600">
                            {workout.day}{workout.duration ? ` • ${workout.duration}` : ''} • {workout.workoutType === 'run' ? `🏃 ${describeRun(workout)}` : `${workout.exercises?.length || 0} exercises`}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setWorkoutForm({
                                ...emptyWorkoutForm(),
                                ...workout,
                                equipment: Array.isArray(workout.equipment) ? workout.equipment : []
                              });
                              setEditingWorkoutIndex(idx);
                              setCurrentWorkout({});
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Delete "${workout.title}"?`)) {
                                const updatedWeeks = [...program.weeks];
                                updatedWeeks[currentWeek].workouts.splice(idx, 1);
                                setProgram({ ...program, weeks: updatedWeeks });
                              }
                            }}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      <div className="text-xs text-slate-600 space-y-1">
                        {workout.workoutType === 'run' ? (
                          <div>
                            {describeRun(workout)}
                            {workout.targetMode === 'intervals' && workout.recovery && (
                              <span className="text-slate-400"> — recovery: {workout.recovery}</span>
                            )}
                          </div>
                        ) : (
                          (workout.exercises || []).map((ex, i) => (
                            <div key={i}>
                              {i + 1}. {ex.supersetGroup && `[${ex.supersetGroup}] `}{ex.name} - {ex.sets.length} sets
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add New Workout */}
                {currentWorkout === null ? (
                  <button
                    onClick={() => setCurrentWorkout({})}
                    className="w-full border-2 border-dashed border-slate-300 rounded-lg p-6 text-slate-500 hover:border-orange-500 hover:text-orange-500 transition-colors"
                  >
                    + Add Workout
                  </button>
                ) : (
                  <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-6">
                    <h4 className="font-bold text-slate-800 mb-4">
                      {editingWorkoutIndex !== null ? '✏️ Edit Workout' : '➕ New Workout'}
                    </h4>

                    {/* Workout Type: strength or run */}
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {[
                        { key: 'strength', label: '🏋️ Strength' },
                        { key: 'run', label: '🏃 Run' }
                      ].map(t => (
                        <button
                          key={t.key}
                          type="button"
                          onClick={() => setWorkoutForm({ ...workoutForm, workoutType: t.key })}
                          className={`px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-colors ${
                            (workoutForm.workoutType || 'strength') === t.key
                              ? 'bg-orange-500 border-orange-500 text-white'
                              : 'bg-white border-slate-300 text-slate-600 hover:border-orange-300'
                          }`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <input
                        type="text"
                        placeholder="Workout Title *"
                        value={workoutForm.title}
                        onChange={(e) => setWorkoutForm({ ...workoutForm, title: e.target.value })}
                        className="border rounded px-3 py-2"
                      />
                      <input
                        type="text"
                        placeholder="Day (e.g., Monday)"
                        value={workoutForm.day}
                        onChange={(e) => setWorkoutForm({ ...workoutForm, day: e.target.value })}
                        className="border rounded px-3 py-2"
                      />
                    </div>

                    <div className="mb-3">
                      <textarea
                        placeholder="Workout Description (what members will see)"
                        value={workoutForm.description || ''}
                        onChange={(e) => setWorkoutForm({ ...workoutForm, description: e.target.value })}
                        className="w-full border rounded px-3 py-2 text-sm"
                        rows="2"
                      />
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <input
                        type="text"
                        placeholder="Duration (e.g., 45 min)"
                        value={workoutForm.duration}
                        onChange={(e) => setWorkoutForm({ ...workoutForm, duration: e.target.value })}
                        className="border rounded px-3 py-2"
                      />
                      <select
                        value={workoutForm.difficulty}
                        onChange={(e) => setWorkoutForm({ ...workoutForm, difficulty: e.target.value })}
                        className="border rounded px-3 py-2"
                      >
                        <option>Beginner</option>
                        <option>Intermediate</option>
                        <option>Advanced</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Equipment (comma separated)"
                        value={Array.isArray(workoutForm.equipment) ? workoutForm.equipment.join(', ') : workoutForm.equipment}
                        onChange={(e) => setWorkoutForm({ ...workoutForm, equipment: e.target.value })}
                        className="border rounded px-3 py-2"
                      />
                    </div>

                    {workoutForm.workoutType === 'run' ? (
                      /* RUN WORKOUT FIELDS */
                      <div className="bg-blue-50 p-4 rounded border border-blue-200 mb-4 space-y-3">
                        <h5 className="text-sm font-semibold text-slate-700">🏃 Run Details</h5>

                        <div>
                          <label className="text-xs font-semibold text-slate-700 block mb-1">Run Type</label>
                          <select
                            value={workoutForm.runType}
                            onChange={(e) => setWorkoutForm({ ...workoutForm, runType: e.target.value })}
                            className="w-full border rounded px-3 py-2 text-sm bg-white"
                          >
                            {RUN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>

                        <div>
                          <label className="text-xs font-semibold text-slate-700 block mb-1">Target</label>
                          <div className="grid grid-cols-3 gap-2">
                            {[['distance', 'Distance'], ['duration', 'Duration'], ['intervals', 'Intervals']].map(([key, label]) => (
                              <button
                                key={key}
                                type="button"
                                onClick={() => setWorkoutForm({ ...workoutForm, targetMode: key })}
                                className={`px-3 py-2 rounded border text-xs font-semibold transition-colors ${
                                  workoutForm.targetMode === key
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400'
                                }`}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {workoutForm.targetMode === 'distance' && (
                          <div className="flex gap-2">
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              placeholder="Distance"
                              value={workoutForm.distance}
                              onChange={(e) => setWorkoutForm({ ...workoutForm, distance: e.target.value })}
                              className="border rounded px-3 py-2 text-sm flex-1"
                            />
                            <select
                              value={workoutForm.distanceUnit}
                              onChange={(e) => setWorkoutForm({ ...workoutForm, distanceUnit: e.target.value })}
                              className="border rounded px-2 py-2 text-sm bg-white"
                            >
                              <option value="km">km</option>
                              <option value="mi">mi</option>
                            </select>
                          </div>
                        )}

                        {workoutForm.targetMode === 'duration' && (
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="0"
                              placeholder="45"
                              value={workoutForm.runDuration}
                              onChange={(e) => setWorkoutForm({ ...workoutForm, runDuration: e.target.value })}
                              className="border rounded px-3 py-2 text-sm w-28"
                            />
                            <span className="text-xs text-slate-600">minutes</span>
                          </div>
                        )}

                        {workoutForm.targetMode === 'intervals' && (
                          <div className="space-y-2">
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="0"
                                placeholder="Reps"
                                value={workoutForm.intervalReps}
                                onChange={(e) => setWorkoutForm({ ...workoutForm, intervalReps: e.target.value })}
                                className="border rounded px-3 py-2 text-sm w-20"
                              />
                              <span className="text-sm text-slate-600">×</span>
                              <input
                                type="number"
                                min="0"
                                placeholder="Distance"
                                value={workoutForm.intervalDistance}
                                onChange={(e) => setWorkoutForm({ ...workoutForm, intervalDistance: e.target.value })}
                                className="border rounded px-3 py-2 text-sm flex-1"
                              />
                              <select
                                value={workoutForm.intervalUnit}
                                onChange={(e) => setWorkoutForm({ ...workoutForm, intervalUnit: e.target.value })}
                                className="border rounded px-2 py-2 text-sm bg-white"
                              >
                                <option value="m">m</option>
                                <option value="km">km</option>
                              </select>
                            </div>
                            <input
                              type="text"
                              placeholder="Recovery (e.g., 400m easy jog, 90s walk)"
                              value={workoutForm.recovery}
                              onChange={(e) => setWorkoutForm({ ...workoutForm, recovery: e.target.value })}
                              className="w-full border rounded px-3 py-2 text-sm"
                            />
                          </div>
                        )}

                        <div>
                          <label className="text-xs font-semibold text-slate-700 block mb-1">
                            Effort (members self-select pace)
                          </label>
                          <select
                            value={workoutForm.effort}
                            onChange={(e) => setWorkoutForm({ ...workoutForm, effort: e.target.value })}
                            className="w-full border rounded px-3 py-2 text-sm bg-white"
                          >
                            {RUN_EFFORTS.map(e => <option key={e} value={e}>{e}</option>)}
                          </select>
                        </div>
                      </div>
                    ) : (
                    <>
                    {/* Exercises in workout */}
                    {workoutForm.exercises.length > 0 && (
                      <div className="mb-4">
                        <h5 className="text-sm font-semibold text-slate-700 mb-2">Exercises ({workoutForm.exercises.length}) - Drag to reorder</h5>
                        <div className="space-y-0">
                          {workoutForm.exercises.map((ex, idx) => {
                            const prevEx = idx > 0 ? workoutForm.exercises[idx - 1] : null;
                            const nextEx = idx < workoutForm.exercises.length - 1 ? workoutForm.exercises[idx + 1] : null;
                            const isLinkedToPrev = prevEx && prevEx.supersetGroup && ex.supersetGroup && 
                              prevEx.supersetGroup.charAt(0) === ex.supersetGroup.charAt(0);
                            const isLinkedToNext = nextEx && nextEx.supersetGroup && ex.supersetGroup && 
                              nextEx.supersetGroup.charAt(0) === ex.supersetGroup.charAt(0);
                            const isInSuperset = !!ex.supersetGroup;
                            const isFirstInGroup = isInSuperset && !isLinkedToPrev;

                            return (
                              <React.Fragment key={ex.id}>
                                {/* Superset group header */}
                                {isFirstInGroup && (
                                  <div className="flex items-center gap-1.5 mb-1 mt-2">
                                    <span className="text-[10px] font-bold text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">
                                      🔗 Superset {ex.supersetGroup.charAt(0)}
                                    </span>
                                    <div className="flex-1 h-px bg-purple-200"></div>
                                  </div>
                                )}

                                {/* Exercise card */}
                                <div 
                                  draggable
                                  onDragStart={() => setDraggedExerciseIndex(idx)}
                                  onDragOver={(e) => e.preventDefault()}
                                  onDrop={() => {
                                    if (draggedExerciseIndex === null) return;
                                    const newExercises = [...workoutForm.exercises];
                                    const [draggedItem] = newExercises.splice(draggedExerciseIndex, 1);
                                    newExercises.splice(idx, 0, draggedItem);
                                    setWorkoutForm({ ...workoutForm, exercises: newExercises });
                                    setDraggedExerciseIndex(null);
                                  }}
                                  onDragEnd={() => setDraggedExerciseIndex(null)}
                                  className={`p-3 border flex items-center justify-between cursor-move transition-all ${
                                    editingExerciseIndex === idx
                                      ? 'bg-blue-50 border-blue-400 ring-2 ring-blue-200'
                                      : draggedExerciseIndex === idx 
                                        ? 'bg-white opacity-50 scale-95' 
                                        : isInSuperset
                                          ? 'bg-purple-50 border-purple-300 hover:shadow-md'
                                          : 'bg-white hover:shadow-md'
                                  } ${isLinkedToPrev ? 'border-t-0 rounded-b-lg rounded-t-none' : 'rounded-t-lg'} ${isLinkedToNext ? 'border-b-0 rounded-b-none' : 'rounded-b-lg'} ${!isLinkedToPrev && !isLinkedToNext && !isInSuperset ? 'rounded-lg mt-2' : ''}`}
                                >
                                  <div className="flex items-center gap-2">
                                    <div className="text-slate-400 cursor-grab active:cursor-grabbing">
                                      ⋮⋮
                                    </div>
                                    <div>
                                      <div className="text-sm font-medium">
                                        {ex.supersetGroup && <span className="text-purple-600 font-bold">{ex.supersetGroup} </span>}
                                        {ex.name}
                                      </div>
                                      <div className="text-xs text-slate-600">
                                        {ex.sets.length} sets • Rest: {ex.restTime}
                                        {ex.tempo && ` • Tempo: ${ex.tempo}`}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => {
                                        setExerciseForm({
                                          name: ex.name,
                                          sets: ex.sets,
                                          restTime: ex.restTime,
                                          supersetGroup: ex.supersetGroup || '',
                                          tempo: ex.tempo || '',
                                          videoUrl: ex.videoUrl || '',
                                          instructions: ex.instructions || '',
                                          muscleGroups: ex.muscleGroups || ''
                                        });
                                        setSearchQuery(ex.name);
                                        setEditingExerciseIndex(idx);
                                      }}
                                      className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => removeExercise(ex.id)}
                                      className="text-red-600 hover:text-red-800 text-xs"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>

                                {/* Link / Unlink button between exercises */}
                                {idx < workoutForm.exercises.length - 1 && (
                                  <div className="flex items-center justify-center py-1">
                                    {isLinkedToNext ? (
                                      <button
                                        onClick={() => unlinkExercises(idx)}
                                        className="flex items-center gap-1 px-3 py-1 text-[11px] font-semibold text-purple-600 bg-purple-100 hover:bg-purple-200 rounded-full transition-colors"
                                        title="Unlink superset"
                                      >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.102 1.101" />
                                        </svg>
                                        Linked — click to unlink
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => linkExercises(idx)}
                                        className="flex items-center gap-1 px-3 py-1 text-[11px] font-medium text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-full border border-dashed border-slate-300 hover:border-purple-400 transition-colors"
                                        title="Link as superset"
                                      >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.102 1.101" />
                                        </svg>
                                        Link as superset
                                      </button>
                                    )}
                                  </div>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Add Exercise Form */}
                    <div className="bg-white p-4 rounded border mb-4">
                      <h5 className="text-sm font-semibold text-slate-700 mb-3">Add Exercise</h5>
                      
                      {/* Exercise name with autocomplete */}
                      <div className="mb-3 relative">
                        <input
                          type="text"
                          placeholder="Start typing exercise name..."
                          value={searchQuery || exerciseForm.name}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setExerciseForm({ ...exerciseForm, name: e.target.value });
                            setShowSuggestions(e.target.value.length > 0);
                          }}
                          onFocus={() => setShowSuggestions(searchQuery.length > 0)}
                          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                          className="w-full border rounded px-3 py-2"
                        />
                        
                        {showSuggestions && filteredExercises.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {filteredExercises.slice(0, 5).map(ex => (
                              <button
                                key={ex.id}
                                type="button"
                                onClick={() => selectFromLibrary(ex)}
                                className="w-full text-left px-4 py-2 hover:bg-blue-50 border-b last:border-b-0"
                              >
                                <div className="font-medium text-sm">{ex.name}</div>
                                <div className="text-xs text-slate-600">
                                  {Array.isArray(ex.muscleGroups) ? ex.muscleGroups.join(', ') : (ex.muscleGroups || '')}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <input
                          type="text"
                          placeholder="Superset (A1, A2...)"
                          value={exerciseForm.supersetGroup}
                          onChange={(e) => setExerciseForm({ ...exerciseForm, supersetGroup: e.target.value.toUpperCase() })}
                          className="border rounded px-2 py-1 text-sm"
                          maxLength="2"
                        />
                        <input
                          type="text"
                          placeholder="Tempo (3-1-1-0)"
                          value={exerciseForm.tempo}
                          onChange={(e) => setExerciseForm({ ...exerciseForm, tempo: e.target.value })}
                          className="border rounded px-2 py-1 text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Rest (60s)"
                          value={exerciseForm.restTime}
                          onChange={(e) => setExerciseForm({ ...exerciseForm, restTime: e.target.value })}
                          className="border rounded px-2 py-1 text-sm"
                        />
                      </div>

                      {/* Sets */}
                      <div className="mb-3">
                        <label className="text-xs font-semibold text-slate-700 block mb-2">Sets</label>
                        {exerciseForm.sets.map((set, idx) => (
                          <div key={idx} className="flex gap-2 mb-2 items-center">
                            <span className="text-xs text-slate-600 w-12">Set {set.setNumber}</span>
                            <input
                              type="text"
                              placeholder="Reps"
                              value={set.reps}
                              onChange={(e) => {
                                const sets = [...exerciseForm.sets];
                                sets[idx].reps = e.target.value;
                                setExerciseForm({ ...exerciseForm, sets });
                              }}
                              className="border rounded px-2 py-1 text-sm flex-1"
                            />
                            <select
                              value={set.type}
                              onChange={(e) => {
                                const sets = [...exerciseForm.sets];
                                sets[idx].type = e.target.value;
                                setExerciseForm({ ...exerciseForm, sets });
                              }}
                              className="border rounded px-2 py-1 text-sm"
                            >
                              <option value="normal">Normal</option>
                              <option value="warmup">Warm-up</option>
                              <option value="dropset">Drop Set</option>
                              <option value="failure">Failure</option>
                            </select>
                            <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={set.isTimed}
                                onChange={(e) => {
                                  const sets = [...exerciseForm.sets];
                                  sets[idx].isTimed = e.target.checked;
                                  setExerciseForm({ ...exerciseForm, sets });
                                }}
                              />
                              Timed
                            </label>
                            {exerciseForm.sets.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const sets = exerciseForm.sets.filter((_, i) => i !== idx);
                                  // Renumber sets
                                  const renumberedSets = sets.map((s, i) => ({ ...s, setNumber: i + 1 }));
                                  setExerciseForm({ ...exerciseForm, sets: renumberedSets });
                                }}
                                className="text-red-600 hover:text-red-800 text-sm font-bold"
                                title="Remove set"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            setExerciseForm({
                              ...exerciseForm,
                              sets: [...exerciseForm.sets, {
                                setNumber: exerciseForm.sets.length + 1,
                                reps: '12',
                                type: 'normal',
                                isTimed: false
                              }]
                            });
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          + Add Set
                        </button>
                      </div>

                      {/* Video URL */}
                      <div className="mb-3">
                        <label className="text-xs font-semibold text-slate-700 block mb-1">
                          Video URL (optional)
                        </label>
                        <input
                          type="url"
                          placeholder="https://youtube.com/... or https://vimeo.com/..."
                          value={exerciseForm.videoUrl}
                          onChange={(e) => setExerciseForm({ ...exerciseForm, videoUrl: e.target.value })}
                          className="w-full border rounded px-3 py-2 text-sm"
                        />
                      </div>

                      {/* Muscle Groups */}
                      <div className="mb-3">
                        <label className="text-xs font-semibold text-slate-700 block mb-1">
                          Muscle Groups (optional)
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., Glutes, Hamstrings, Quads"
                          value={exerciseForm.muscleGroups}
                          onChange={(e) => setExerciseForm({ ...exerciseForm, muscleGroups: e.target.value })}
                          className="w-full border rounded px-3 py-2 text-sm"
                        />
                      </div>

                      {/* Instructions */}
                      <div className="mb-3">
                        <label className="text-xs font-semibold text-slate-700 block mb-1">
                          Instructions (optional)
                        </label>
                        <textarea
                          placeholder="Exercise instructions for members..."
                          value={exerciseForm.instructions}
                          onChange={(e) => setExerciseForm({ ...exerciseForm, instructions: e.target.value })}
                          rows="2"
                          className="w-full border rounded px-3 py-2 text-sm"
                        />
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={addExercise}
                          disabled={!exerciseForm.name}
                          className={`flex-1 text-white px-4 py-2 rounded disabled:bg-gray-400 ${
                            editingExerciseIndex !== null
                              ? 'bg-blue-600 hover:bg-blue-700'
                              : 'bg-green-600 hover:bg-green-700'
                          }`}
                        >
                          {editingExerciseIndex !== null ? '✓ Update Exercise' : '✓ Add Exercise'}
                        </button>
                        {editingExerciseIndex !== null && (
                          <button
                            onClick={() => {
                              setEditingExerciseIndex(null);
                              setExerciseForm({
                                name: '',
                                sets: [{ setNumber: 1, reps: '12', type: 'normal', isTimed: false }],
                                restTime: '60s',
                                supersetGroup: '',
                                tempo: '',
                                videoUrl: '',
                                instructions: '',
                                muscleGroups: ''
                              });
                              setSearchQuery('');
                            }}
                            className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
                          >
                            Cancel Edit
                          </button>
                        )}
                        <button
                          onClick={saveToLibrary}
                          disabled={!exerciseForm.name}
                          className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 disabled:bg-gray-400"
                          title="Save to library for reuse"
                        >
                          💾 Save to Library
                        </button>
                      </div>
                    </div>
                    </>
                    )}

                    <div className="flex gap-2">
                      <button
                        onClick={saveWorkout}
                        disabled={!workoutForm.title || (workoutForm.workoutType !== 'run' && workoutForm.exercises.length === 0)}
                        className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
                      >
                        {editingWorkoutIndex !== null ? '💾 Update Workout' : '✓ Save Workout'}
                      </button>
                      <button
                        onClick={() => {
                          setCurrentWorkout(null);
                          setEditingWorkoutIndex(null);
                          setWorkoutForm(emptyWorkoutForm());
                        }}
                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t bg-slate-50 flex justify-between">
          <button
            onClick={() => {
              const counts = getProgramCounts(program);
              if (counts.totalWorkouts > 0 || program.title) {
                const confirmClose = window.confirm(
                  `⚠️ You have unsaved work:\n\n` +
                  `${program.title || 'Untitled'}: ${counts.totalWorkouts} workouts, ${counts.totalExercises} exercises\n\n` +
                  `Your draft is backed up locally, but closing will discard any changes not yet published.\n\nAre you sure you want to close?`
                );
                if (!confirmClose) return;
              }
              onClose();
            }}
            className="px-6 py-2 text-slate-600 hover:text-slate-800"
          >
            Cancel
          </button>
          
          <div className="flex gap-3">
            {step === 2 && (
              <button
                onClick={() => setStep(1)}
                className="px-6 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300"
              >
                ← Back
              </button>
            )}
            
            {step === 1 ? (
              <button
                onClick={() => setStep(2)}
                disabled={!program.title || !program.description}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400"
              >
                Next: Build Workouts →
              </button>
            ) : (
              <button
                onClick={() => {
                  const counts = getProgramCounts(program);
                  // Warn if any weeks are empty
                  const emptyWeeks = (program.weeks || []).filter(w => !w.workouts || w.workouts.length === 0);
                  if (emptyWeeks.length > 0) {
                    const proceed = window.confirm(
                      `⚠️ Warning: ${emptyWeeks.length} week(s) have no workouts.\n\nAre you sure you want to save?`
                    );
                    if (!proceed) return;
                  }
                  // Show confirmation with counts
                  const confirmed = window.confirm(
                    `📋 Save Program Summary:\n\n` +
                    `Title: ${program.title}\n` +
                    `Level: ${program.level}\n` +
                    `Weeks: ${counts.totalWeeks}\n` +
                    `Total Workouts: ${counts.totalWorkouts}\n` +
                    `Total Exercises: ${counts.totalExercises}\n\n` +
                    `Does this look correct?`
                  );
                  if (confirmed) {
                    onSave(program);
                  }
                }}
                className="px-8 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-bold text-lg"
              >
                ✨ Publish Program
              </button>
            )}
          </div>
        </div>

        {/* Create New Exercise Modal */}
        {showCreateExerciseModal && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-slate-800 mb-4">Create New Exercise</h3>
              
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                createNewExercise({
                  name: formData.get('name'),
                  muscleGroups: formData.get('muscleGroups'),
                  videoUrl: formData.get('videoUrl'),
                  instructions: formData.get('instructions'),
                  defaultRestTime: formData.get('restTime') || '60s',
                  defaultTempo: formData.get('tempo') || ''
                });
              }}>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                      Exercise Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="e.g., Hip Thrusts"
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                      Muscle Groups *
                    </label>
                    <input
                      type="text"
                      name="muscleGroups"
                      required
                      placeholder="e.g., Glutes, Hamstrings"
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                      Video URL (optional)
                    </label>
                    <input
                      type="url"
                      name="videoUrl"
                      placeholder="https://youtube.com/..."
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">
                        Rest Time
                      </label>
                      <input
                        type="text"
                        name="restTime"
                        placeholder="60s"
                        className="w-full border rounded px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">
                        Tempo
                      </label>
                      <input
                        type="text"
                        name="tempo"
                        placeholder="3-1-1-0"
                        className="w-full border rounded px-3 py-2"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">
                      Instructions (optional)
                    </label>
                    <textarea
                      name="instructions"
                      placeholder="Exercise instructions..."
                      rows="3"
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
                  >
                    Create Exercise
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateExerciseModal(false)}
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleProgramBuilder;
